// frontend/my-react-app/src/TimeSlotContainer.jsx
// Created: 2025-09-24
// Updated: 2025-10-13 — added account switching support
// Updated: 2026-03-09 — removed duplicate apiDateString variable; DailyNotes now uses apiDateFormat
// Dashboard component — groups and displays daily medications by time slot.

import React, { useState, useEffect } from 'react';
import MedicationComponent from './MedicationComponent';
import './TimeSlotContainer.css';
import DailyNotes from './DailyNotes';
import { fetchScheduledMedications, updateMedicationStatus, fetchAsNeededLogs, createAsNeededLog } from './services/api';
import { useAuth } from '@clerk/clerk-react';
import { usePet } from './contexts/PetContext';

const TimeSlotContainer = ({ selectedDate }) => {
  const { getToken } = useAuth();
  const { selectedPet, pets } = usePet();

  // Format date for API (YYYY-MM-DD)
  const apiDateFormat = selectedDate ? 
    `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` 
    : null;
  
  // State for API data
  const [scheduledMedications, setScheduledMedications] = useState({});
  const [medicationStatuses, setMedicationStatuses] = useState({});
  const [asNeededMedications, setAsNeededMedications] = useState([]);
  const [asNeededCounts, setAsNeededCounts] = useState({}); 
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch medications from API when date OR selected pet changes
  useEffect(() => {
    if (!selectedPet) return;

    const loadMedications = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await fetchScheduledMedications(apiDateFormat, getToken, selectedPet.id);
        console.log('API Response:', data); 
        
        // Set the scheduled medications (already grouped by time slot)
        setScheduledMedications(data.scheduledMedications || {});
        
        // Set as-needed medications
        setAsNeededMedications(data.asNeededMedications || []);
        
        // Fetch counts for each as-needed medication
        const counts = {};
        const asNeededMeds = data.asNeededMedications || [];
        await Promise.all(
          asNeededMeds.map(async (med) => {
            try {
              const logData = await fetchAsNeededLogs(med.id, apiDateFormat, getToken, selectedPet.id);
              counts[med.id] = logData.count || 0;
            } catch (err) {
              console.error(`Failed to fetch count for ${med.medicationName}:`, err);
              counts[med.id] = 0; // Default to 0 on error
            }
          })
        );
        setAsNeededCounts(counts);

        // Build status map from the fetched data
        const statuses = {};
        
        // Add scheduled medication statuses
        Object.values(data.scheduledMedications || {}).forEach(timeSlotMeds => {
          timeSlotMeds.forEach(med => {
            statuses[med.log_id] = med.status || 'pending';
          });
        });
        
        // Add as-needed medication statuses
        (data.asNeededMedications || []).forEach(med => {
          statuses[med.id] = med.status || 'pending';
        });
        
        setMedicationStatuses(statuses);
      } catch (err) {
        console.error('Failed to load medications:', err);
        setError('Failed to load medications. Please try again.');
        // Fall back to empty state
        setScheduledMedications({});
        setAsNeededMedications([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadMedications();
  }, [apiDateFormat, selectedPet, getToken]);

  // Handle SCHEDULED medication status changes (taken/skipped/missed)
  // Updates existing daily_medication_logs entries created by midnight job
  // Used by: Scheduled medications in time slot sections
  const handleStatusChange = async (logId, newStatus) => {
    // Update local state immediately for responsive UI
    setMedicationStatuses(prev => ({
      ...prev,
      [logId]: newStatus
    }));
    
    try {
      // Call API to persist the change
      await updateMedicationStatus(logId, newStatus, getToken);
    } catch (error) {
      console.error('Failed to update status:', error);
      // Optionally revert the status on error
      // Could also show a toast notification here
    }
  };

  // Handle as-needed medication "take" action
  const handleAsNeededTake = async (medicationId) => {
    try {
      const result = await createAsNeededLog(medicationId, apiDateFormat, getToken, selectedPet?.id);
      
      // Update the count immediately
      setAsNeededCounts(prev => ({
        ...prev,
        [medicationId]: result.today_count
      }));
      
      console.log(`As-needed med logged: ${result.message}, Count: ${result.today_count}`);
    } catch (error) {
      console.error('Failed to log as-needed medication:', error);
    }
  }; 

  // Calculate progress
  const timeSlots = Object.keys(scheduledMedications);
  const scheduledMedicationsList = Object.values(scheduledMedications).flat();
  const totalScheduledMedications = scheduledMedicationsList.length;
  const takenScheduledMedications = scheduledMedicationsList.filter(
    med => medicationStatuses[med.log_id] === 'taken'
  ).length;
  const hasScheduledMedications = scheduledMedicationsList.length > 0;
  
  // Calculate percentage (rounded to whole number)
  const percentage = totalScheduledMedications > 0
    ? Math.round((takenScheduledMedications / totalScheduledMedications) * 100)
    : 0;

  // Trigger confetti when 100% completion is reached
  useEffect(() => {
    if (percentage === 100 && hasScheduledMedications && window.confetti) {
      // Fire confetti animation
      window.confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [percentage, hasScheduledMedications]);

  // No pet selected (e.g. user has no pets)
  if (!selectedPet) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#6b7280'
      }}>
        <p style={{ fontSize: '18px', margin: 0 }}>
          {pets.length === 0 ? 'Add a pet to get started.' : 'Select a pet.'}
        </p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#6b7280'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <p style={{
          fontSize: '18px',
          margin: 0
        }}>
          Loading medications...
        </p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#dc2626'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <p style={{
          fontSize: '18px',
          margin: 0
        }}>
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="time-slot-container">
      {/* PROGRESS BAR - Only show if there are scheduled medications */}
      {hasScheduledMedications && (
        <div className="progress-section">
          <div className="progress-header">
            <span className="progress-count">
              {takenScheduledMedications} of {totalScheduledMedications} medications taken ({percentage}%)
            </span>
          </div>
          
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill" 
              style={{ 
                width: `${percentage}%`,
                backgroundColor: percentage === 100 ? '#10b981' : '#3b82f6'
              }}
            />
          </div>
        </div>
      )}

      {!hasScheduledMedications && asNeededMedications.length === 0 ? (
        <>
          {/* Empty state */}
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📋</div>
            <p style={{
              fontSize: '18px',
              margin: 0
            }}>
              No medications scheduled for this day.
            </p>
          </div>
        </>
      ) : (
        <>
          {/* SCHEDULED MEDICATIONS BY TIME SLOT */}
          {timeSlots.map((timeSlot) => (
            <div key={timeSlot} className="time-slot">
              <div className="time-slot-header">
                <h2 className="time-slot-title">{timeSlot}</h2>
                <span className="medication-count">
                  {scheduledMedications[timeSlot].length} med{scheduledMedications[timeSlot].length > 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="medications-list">
                {scheduledMedications[timeSlot].map((medication) => (
                  <MedicationComponent
                    key={`${medication.id}-${timeSlot}`}
                    medicationId={medication.log_id}
                    medicationName={medication.medicationName}
                    totalDosage={medication.totalDosage}
                    quantityToTake={medication.quantityToTake}
                    unit={medication.unit}
                    instructions={medication.instructions}
                    imageUrl={medication.imageUrl}
                    status={medicationStatuses[medication.log_id]}
                    onStatusChange={handleStatusChange} // Updates existing log status
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {/* AS NEEDED MEDICATIONS SECTION */}
      {asNeededMedications.length > 0 && (
        <div className="as-needed-section">
          <div className="time-slot-header">
            <h2 className="time-slot-title">Take As Needed</h2>
          </div>
          
          <div className="medications-list">
            {asNeededMedications.map((medication) => (
              <MedicationComponent
                key={medication.id}
                medicationId={medication.id}
                medicationName={medication.medicationName}
                totalDosage={medication.totalDosage}
                quantityToTake={medication.quantityToTake}
                unit={medication.unit}
                instructions={medication.instructions}
                imageUrl={medication.imageUrl}
                status={medicationStatuses[medication.id]}
                onStatusChange={() => handleAsNeededTake(medication.id)}  // Creates new log via POST
                isAsNeeded={true}
                takenCount={asNeededCounts[medication.id] || 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* DAILY NOTES - Always show at the bottom */}
      <DailyNotes date={apiDateFormat} />
    </div>
  );
};

export default TimeSlotContainer;